// Ambient "is the user actively editing?" detection, for Mirror (pageFollow).
//
// When a followed collaborator turns the page, we must not yank the view out from
// under someone mid-keystroke or mid-drag. Rather than ask every cue/annotation
// component to report its own edit state (six components, and a single missed
// end-call would wedge sync off permanently), we read the ambient DOM: what has
// focus, whether a pointer is genuinely down, and whether a modal is open.
//
// Deliberate exclusions, each of which is a real bug if you get it wrong:
//
//  - <select> and readonly inputs are NOT editing. A <select> keeps focus after a
//    choice is made, and every md-select renders a *readonly* <input>. Counting
//    them would mean one layer pick or zoom change leaves the user permanently
//    "editing", silently killing sync forever. A page change cannot eat a
//    keystroke that isn't being typed.
//
//  - A plain click is NOT editing. Clicking the canvas to scroll or select text
//    holds the pointer ~80-150ms. Only a pointer that started on something
//    editable/draggable, moved past a drag threshold, or has been held past
//    HOLD_MS counts.
//
//  - The app's own alert banner is NOT a user modal. views/project.vue renders an
//    md-dialog.alert-banner-dialog at every viewer during a collaborator's page
//    op; treating it as "the user is editing" would pause sync for something the
//    user didn't do.

const HOLD_MS = 250;          // below this, a pointer is a click, not an edit
const DRAG_THRESHOLD_PX = 4;  // movement past this is a drag, not a click
const STALE_MS = 3000;        // a pointer held this long with no movement is untrusted
// Tabbing between fields fires focusout on the old field a few ms before the new
// one takes focus; in that window document.activeElement is the page body and a
// focus-only check reads "not editing". Anything asking the editing question
// during that gap (a resume timer, or a leader's page arriving) would act on a
// false negative. So editing stays sticky for this long after the last blur out
// of an editable — long enough to bridge any field-to-field transition.
const FOCUS_GRACE_MS = 500;

const TEXT_INPUT_TYPES = [
    'text', 'search', 'tel', 'url', 'email', 'password', 'number', ''
];

// Verified against the components that actually exist: cues, notes and both cue
// markers are vue-draggable-resizable roots (its own root class is .vdr, and the
// app's classes merge onto it), and .drag is the cue's explicit drag-handle
// (cue.vue's drag-handle=".drag", rendered by cue-header.vue).
const EDITABLE_SELECTOR = [
    'input', 'textarea', '[contenteditable]',
    '.vdr', '.drag',
    '.ql-cue', '.ql-note',
    '.ql-position-cue-marker', '.ql-selection-cue-marker'
].join(',');

let initialized = false;
let pointerDownAt = 0;
let pointerOnEditable = false;
let pointerMoved = false;
let pointerStartX = 0;
let pointerStartY = 0;
let lastEditableBlurAt = 0;

function clearPointer() {
    pointerDownAt = 0;
    pointerOnEditable = false;
    pointerMoved = false;
}

function onPointerDown(e) {
    pointerDownAt = Date.now();
    pointerMoved = false;
    pointerStartX = e.clientX;
    pointerStartY = e.clientY;
    pointerOnEditable = !!(e.target && e.target.closest && e.target.closest(EDITABLE_SELECTOR));
}

function onPointerMove(e) {
    if (!pointerDownAt || pointerMoved) return;
    const dx = Math.abs(e.clientX - pointerStartX);
    const dy = Math.abs(e.clientY - pointerStartY);
    if (dx > DRAG_THRESHOLD_PX || dy > DRAG_THRESHOLD_PX) {
        pointerMoved = true;
    }
}

// Stamp the moment focus leaves an editable target. focusout bubbles (unlike
// blur), so a capture-phase document listener sees every field exit — including
// the one at the start of a tab, which is exactly the transition we must not
// misread as "done editing".
function onFocusOut(e) {
    const el = e.target;
    if (el && el.closest && el.closest(EDITABLE_SELECTOR)) {
        lastEditableBlurAt = Date.now();
    }
}

export function isPointerEditing() {
    if (!pointerDownAt) return false;
    const held = Date.now() - pointerDownAt;
    // A pointer we've heard nothing about for seconds is a lost pointerup, not an
    // edit. Never suppress on a pointer we don't trust.
    if (held > STALE_MS && !pointerMoved) {
        clearPointer();
        return false;
    }
    return pointerOnEditable || pointerMoved || held > HOLD_MS;
}

export function isTextEntryFocused() {
    const el = document.activeElement;
    if (!el) return false;
    if (el.tagName === 'TEXTAREA') return !el.readOnly && !el.disabled;
    if (el.isContentEditable) return true;
    if (el.tagName === 'INPUT') {
        if (el.readOnly || el.disabled) return false;
        return TEXT_INPUT_TYPES.indexOf((el.type || '').toLowerCase()) !== -1;
    }
    return false;
}

export function isModalOpen() {
    return !!document.querySelector(
        '.md-dialog-container.md-active:not(.alert-banner-dialog)'
    );
}

// The cue and note components already publish their own edit state to the DOM:
// cue.vue sets .cue-editing while isEditing/isEditingDescription/isEditingTime,
// and note.vue sets .note-editing. This is broader and more reliable than focus
// alone — a cue open for editing is "being edited" even in the moments when no
// single field holds focus (between fields, or after a blur that didn't close
// the panel). Reading it costs nothing and needs no changes in those components.
export function isElementEditing() {
    return !!document.querySelector('.cue-editing, .note-editing');
}

// True for a short grace after leaving an editable, so a field-to-field tab
// never presents as "not editing" in the transition gap.
export function isInEditGrace() {
    return lastEditableBlurAt > 0 && (Date.now() - lastEditableBlurAt) < FOCUS_GRACE_MS;
}

export function isActivelyEditing() {
    return isPointerEditing()
        || isTextEntryFocused()
        || isElementEditing()
        || isModalOpen()
        || isInEditGrace();
}

export function initEditingGuard() {
    if (initialized) return;
    initialized = true;
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('pointermove', onPointerMove, true);
    document.addEventListener('pointerup', clearPointer, true);
    document.addEventListener('pointercancel', clearPointer, true);
    document.addEventListener('focusout', onFocusOut, true);
    window.addEventListener('blur', clearPointer);
}

export function teardownEditingGuard() {
    if (!initialized) return;
    initialized = false;
    document.removeEventListener('pointerdown', onPointerDown, true);
    document.removeEventListener('pointermove', onPointerMove, true);
    document.removeEventListener('pointerup', clearPointer, true);
    document.removeEventListener('pointercancel', clearPointer, true);
    document.removeEventListener('focusout', onFocusOut, true);
    window.removeEventListener('blur', clearPointer);
    clearPointer();
    lastEditableBlurAt = 0;
}


// WEBPACK FOOTER //
// ./src/lib/editingGuard.js